/**
 * Pure utility functions for AdminUnitDetailPage (Step 7.3 Phase 1).
 * NO React, NO hooks, NO JSX.
 * 4 Cat X constants exported alongside their helper functions.
 */
import {
  getUnitOccupancyStatus,
  getRoomOccupancyStatus,
  getActiveTenancyForRoom,
  getFutureTenancyForRoom,
  getUnitContractState,
  getTodayIsoForOccupancy,
  parseIsoDate,
  isTenancyActiveByDates,
  isTenancyFuture,
  isTenancyReservedSlot,
  effectiveTenancyStatus,
} from "../../../utils/unitOccupancyStatus";
import { getPhase4OperationalWarnings } from "../../../utils/unitOperationalIntelligence";
import { tenantDisplayName } from "../../../utils/tenantDisplayName";
import { formatLandlordSummaryLine } from "../../../utils/landlordDisplayFormat";

export function roundCurrency(value) {
  return Math.round(Number(value || 0));
}

export function formatCurrency(value) {
  return `CHF ${roundCurrency(value).toLocaleString("de-CH")}`;
}

export function formatChfOrDash(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "-";
  }
  return formatCurrency(value);
}

export function formatChfNetChange(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "-";
  }
  const n = roundCurrency(value);
  const sign = n >= 0 ? "+" : "−";
  return `${sign} CHF ${Math.abs(n).toLocaleString("de-CH")}`;
}

export function formatPercent(value) {
  return `${Number(value || 0).toFixed(1)}%`;
}

export const LEASE_TYPE_LABELS = {
  open_ended: "Unbefristet",
  fixed_term: "Befristet",
};

export const LEASE_STATUS_LABELS = {
  active: "Aktiv",
  notice_given: "Gekündigt",
  ended: "Beendet",
};

/** When unit.lease_status is empty: same rules as getUnitContractState, for read-only UI only. */
export const DERIVED_LANDLORD_CONTRACT_STATE_LABELS = {
  active: "Laufend",
  expiring_soon: "Endet bald",
  expired: "Abgelaufen",
  ended: "Beendet",
  unknown: "Mietbeginn offen",
};

export const UNIT_COST_TYPE_OPTIONS = [
  "Miete",
  "Nebenkosten",
  "Reinigung",
  "Internet",
  "Sonstiges",
];

export function landlordContractStateDerivedTone(state) {
  if (state === "expiring_soon") return "orange";
  if (state === "expired" || state === "ended") return "rose";
  if (state === "active") return "green";
  return "slate";
}

export function dashEmpties(value) {
  if (value == null || value === "") return "—";
  const s = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  return s;
}

export function landlordLeaseTypeLabel(unit) {
  const k = String(unit.leaseType ?? unit.lease_type ?? "").trim();
  if (!k) return "—";
  return LEASE_TYPE_LABELS[k] || k;
}

export function landlordLeaseContractStatus(unit) {
  return String(unit.leaseStatus ?? unit.lease_status ?? "").trim();
}

export function landlordLeaseStatusBadgeTone(status) {
  if (status === "active") return "green";
  if (status === "notice_given") return "orange";
  if (status === "ended") return "rose";
  return "slate";
}

export function landlordLeaseNotesDisplay(unit) {
  const n = unit.leaseNotes ?? unit.lease_notes;
  if (n == null || String(n).trim() === "") return "—";
  return String(n);
}

export function calendarDaysUntil(dateStr) {
  if (!dateStr || !/^\d{4}-\d{2}-\d{2}/.test(String(dateStr))) return null;
  const y = Number(String(dateStr).slice(0, 4));
  const m = Number(String(dateStr).slice(5, 7)) - 1;
  const day = Number(String(dateStr).slice(8, 10));
  const end = Date.UTC(y, m, day);
  const now = new Date();
  const t0 = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return Math.round((end - t0) / 86400000);
}

export function formatTenancyMoveIn(iso) {
  if (!iso) return "—";
  const s = String(iso);
  return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : s;
}

export function formatTenancyMoveOut(iso) {
  if (!iso) return "—";
  const s = String(iso);
  return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : s;
}

/** Align with AdminTenantDetailPage / backend tenancy_derived_display_status. */
export function tenancyDisplayStatusLabelDeUnit(ds) {
  const k = String(ds || "").toLowerCase();
  if (k === "active") return "Aktiv";
  if (k === "reserved") return "Reserviert";
  if (k === "notice_given") return "Gekündigt";
  if (k === "ended") return "Beendet";
  return "—";
}

export function tenancyMieterTableSortRank(t, todayIso) {
  // 5C.1: a moved-in reserved tenancy is occupying today — rank it with the
  // actives (0), not Historie. Additive; rank-1/rank-2 branches unchanged. Keys
  // off raw status + canonical occupancy, NOT display_status (the backend
  // display_status fix lands the badge; this lands the sort independently).
  const isMovedInReserved =
    String(t?.status || "").trim().toLowerCase() === "reserved" &&
    effectiveTenancyStatus(t, todayIso) === "occupied";
  if (isTenancyActiveByDates(t, todayIso) || isMovedInReserved) return 0;
  if (isTenancyReservedSlot(t, todayIso) || isTenancyFuture(t, todayIso)) return 1;
  return 2;
}

/** Display meta for Mieter table: Aktiv / Reserviert / Geplant / Gekündigt / Beendet */
export function tenancyMieterTableStatusMeta(t, todayIso = getTodayIsoForOccupancy()) {
  const ds = String(t?.display_status || "").trim().toLowerCase();
  if (ds === "active") {
    return { label: "Aktiv", tone: "green" };
  }
  if (ds === "notice_given") {
    return { label: "Gekündigt", tone: "amber" };
  }
  if (ds === "ended") {
    return { label: "Beendet", tone: "slate" };
  }
  if (ds === "reserved") {
    return { label: "Reserviert", tone: "amber" };
  }
  if (isTenancyActiveByDates(t, todayIso)) {
    return { label: "Aktiv", tone: "green" };
  }
  if (isTenancyReservedSlot(t, todayIso)) {
    return { label: "Reserviert", tone: "amber" };
  }
  if (isTenancyFuture(t, todayIso)) {
    return { label: "Geplant", tone: "blue" };
  }
  const st = String(t.status || "").toLowerCase();
  if (st === "ended") {
    return { label: "Beendet", tone: "slate" };
  }
  const mo = parseIsoDate(t.move_out_date);
  if (mo && mo < todayIso) {
    return { label: "Beendet", tone: "slate" };
  }
  if (st) {
    return {
      label: st.charAt(0).toUpperCase() + st.slice(1),
      tone: "slate",
    };
  }
  return { label: "—", tone: "slate" };
}

export const TENANT_DEPOSIT_TYPE_LABELS = {
  bank: "Bank",
  insurance: "Versicherung",
  cash: "Bar",
  none: "Keine",
};

export const TENANT_DEPOSIT_PROVIDER_LABELS = {
  swisscaution: "SwissCaution",
  smartcaution: "SmartCaution",
  firstcaution: "FirstCaution",
  gocaution: "GoCaution",
  other: "Sonstige",
};

export function tenantDepositTypeLabel(raw) {
  if (!raw || typeof raw !== "string") return "—";
  const k = String(raw).toLowerCase();
  return TENANT_DEPOSIT_TYPE_LABELS[k] || raw;
}

export function tenantDepositProviderLabel(raw) {
  if (!raw || typeof raw !== "string") return "—";
  const k = String(raw).toLowerCase();
  return TENANT_DEPOSIT_PROVIDER_LABELS[k] || raw;
}

export function isUuidLike(s) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    String(s || "").trim()
  );
}

export function landlordDisplayName(l) {
  return formatLandlordSummaryLine(l);
}

export function propertyManagerDisplayName(p) {
  if (!p) return "—";
  const n = String(p.name || "").trim();
  if (n) return n;
  const e = String(p.email || "").trim();
  if (e) return e;
  return p.id || "—";
}

export function formatUnitHeaderLocationLine(unit) {
  const street = String(unit.address || "").trim();
  const zip = String(unit.zip || "").trim();
  const city = String(unit.place || unit.city || "").trim();
  const tail = [zip, city].filter(Boolean).join(" ");
  if (street && tail) return `${street}, ${tail}`;
  if (street) return street;
  if (tail) return tail;
  return "—";
}

export function getUnitPageMainTitle(unit) {
  if (!unit) return "—";
  const shortId = String(unit.shortUnitId ?? unit.short_unit_id ?? "").trim();
  const address = String(unit.address || "").trim();
  if (shortId && address) {
    return `${shortId} · ${address}`;
  }
  const uid = String(unit.unitId || "").trim();
  if (uid && !isUuidLike(uid) && address) {
    return `${uid} · ${address}`;
  }
  return formatUnitHeaderLocationLine(unit);
}

export function formatUnitDocumentDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString("de-CH", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso;
  }
}

export function formatUnitDocumentType(doc) {
  const mime = String(doc.mime_type || "").toLowerCase();
  const name = String(doc.file_name || "");
  const ext = name.includes(".")
    ? (name.split(".").pop() || "").toLowerCase()
    : "";

  if (mime.includes("pdf") || ext === "pdf") return "PDF";
  if (mime.includes("jpeg") || mime.includes("jpg") || ext === "jpg" || ext === "jpeg") return "JPG";
  if (mime.includes("png") || ext === "png") return "PNG";
  if (
    mime.includes("wordprocessingml") ||
    mime.includes("msword") ||
    ext === "docx" ||
    ext === "doc"
  ) {
    return "DOCX";
  }
  if (ext && /^[a-z0-9]+$/i.test(ext)) return ext.toUpperCase();
  return "Datei";
}

export const UNIT_DOCUMENT_CATEGORY_LABELS = {
  rent_contract: "Mietvertrag",
  insurance: "Versicherung",
  internet: "Internet",
  handover: "Übergabe",
  other: "Sonstiges",
};

export function formatUnitDocumentCategoryLabel(category) {
  if (category == null || String(category).trim() === "") return "—";
  const k = String(category).trim();
  return UNIT_DOCUMENT_CATEGORY_LABELS[k] || k;
}

export function getStatusTone(status) {
  if (status === "Belegt") return "green";
  if (status === "Reserviert") return "amber";
  if (status === "Frei") return "rose";
  if (status === "In Reinigung") return "blue";
  if (status === "Blockiert") return "slate";
  if (status === "In Einrichtung") return "orange";
  return "slate";
}

/** Badge tone for getRoomOccupancyStatus keys (frei | reserviert | belegt). */
export function getRoomOccBadgeTone(occ) {
  if (occ === "belegt") return "green";
  if (occ === "reserviert") return "amber";
  if (occ === "frei") return "rose";
  return "slate";
}

export function roomDisplayMoveIn(room, unitTenancies) {
  if (!unitTenancies) {
    const m = room.moveInDate;
    return m && m !== "-" ? String(m) : null;
  }
  const today = getTodayIsoForOccupancy();
  const active = getActiveTenancyForRoom(room, unitTenancies, today);
  const future = getFutureTenancyForRoom(room, unitTenancies, today);
  const fromT = active?.move_in_date ?? future?.move_in_date;
  const d = parseIsoDate(fromT);
  if (d) return d;
  const m = room.moveInDate;
  return m && m !== "-" ? String(m) : null;
}

export function roomDisplayMoveOut(room, unitTenancies) {
  if (!unitTenancies) {
    const m = room.freeFromDate;
    return m && m !== "-" ? String(m) : null;
  }
  const today = getTodayIsoForOccupancy();
  const active = getActiveTenancyForRoom(room, unitTenancies, today);
  const fromT = active?.move_out_date;
  const d = parseIsoDate(fromT);
  if (d) return d;
  const m = room.freeFromDate;
  return m && m !== "-" ? String(m) : null;
}

export function getActiveOrFutureTenancyForRoom(room, unitTenancies) {
  if (!unitTenancies) return null;
  const today = getTodayIsoForOccupancy();
  const active = getActiveTenancyForRoom(room, unitTenancies, today);
  const future = getFutureTenancyForRoom(room, unitTenancies, today);
  return active || future || null;
}

/** Co-Mieter / Solidarhafter from API participants (primary stays on tenancy.tenant_id). */
export function secondaryParticipantLabels(tenancy, tenantNameMap) {
  if (!tenancy) return [];
  const parts = tenancy.participants;
  if (!Array.isArray(parts)) return [];
  const out = [];
  for (const p of parts) {
    if (!p || (p.role !== "co_tenant" && p.role !== "solidarhafter")) continue;
    const tid = String(p.tenant_id || "").trim();
    if (!tid) continue;
    const label =
      tenantDisplayName(p.tenant) ||
      (tenantNameMap && tenantNameMap[tid]) ||
      tid;
    const roleDe = p.role === "co_tenant" ? "Co-Mieter" : "Solidarhafter";
    out.push({ tenantId: tid, label, roleDe });
  }
  return out;
}

export function roomDisplayTenantName(room, unitTenancies, tenantNameMap) {
  if (!unitTenancies) {
    const r = room.tenant;
    return r && r !== "-" ? String(r) : "—";
  }
  const tn = getActiveOrFutureTenancyForRoom(room, unitTenancies);
  if (!tn) {
    const r = room.tenant;
    return r && r !== "-" ? String(r) : "—";
  }
  const name = tenantNameMap[String(tn.tenant_id)];
  if (name && String(name).trim() !== "" && name !== "—") return name;
  const r = room.tenant;
  return r && r !== "-" ? String(r) : "—";
}

export function formatDeShort(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${d}.${m}.${y}`;
}

/** Compact Co-Living room future line (tenancy dates only). */
export function roomCompactFutureSignal(room, unitTenancies) {
  if (!unitTenancies) return null;
  const today = getTodayIsoForOccupancy();
  const active = getActiveTenancyForRoom(room, unitTenancies, today);
  const future = getFutureTenancyForRoom(room, unitTenancies, today);
  const mo = active ? parseIsoDate(active.move_out_date) : null;
  const fi = future ? parseIsoDate(future.move_in_date) : null;
  const parts = [];
  if (mo) parts.push(`frei ab ${formatDeShort(mo)}`);
  if (fi) parts.push(`reserviert ab ${formatDeShort(fi)}`);
  if (parts.length === 0) return null;
  return parts.join(" · ");
}

export function mergeUnitWarningsByText(...lists) {
  const seen = new Set();
  const out = [];
  for (const list of lists) {
    for (const w of list) {
      if (seen.has(w.text)) continue;
      seen.add(w.text);
      out.push(w);
    }
  }
  return out;
}

export function buildUnitWarnings(unit, rooms, metrics, unitTenancies) {
  const warnings = [];
  const isApartment = unit.type === "Apartment";

  const contractState = getUnitContractState(unit);
  const occ =
    unitTenancies != null
      ? getUnitOccupancyStatus(unit, rooms, unitTenancies)
      : null;
  const led = unit.leaseEndDate ?? unit.lease_end_date;

  if (
    contractState === "expiring_soon" &&
    occ != null &&
    (occ === "belegt" || occ === "teilbelegt")
  ) {
    const x = calendarDaysUntil(led);
    const days = x != null && x >= 0 ? x : 0;
    warnings.push({
      tone: "amber",
      text: `Einheit ist belegt, aber Vertrag endet in ${days} Tagen`,
    });
  }

  if (contractState === "ended" && occ != null && occ !== "frei") {
    warnings.push({
      tone: "rose",
      text: "Vertrag beendet, aber Einheit ist noch belegt",
    });
  }

  if (contractState === "expired") {
    warnings.push({
      tone: "amber",
      text: "Vertrag ist abgelaufen, aber noch nicht beendet",
    });
  }

  if (contractState === "unknown") {
    warnings.push({
      tone: "slate",
      text: "Kein Mietbeginn für Vermieter gesetzt",
    });
  }

  const ls = String(unit.leaseStatus ?? unit.lease_status ?? "").trim();
  const ted = unit.terminationEffectiveDate ?? unit.termination_effective_date;
  const rtd = unit.returnedToLandlordDate ?? unit.returned_to_landlord_date;

  if (ls === "notice_given") {
    warnings.push({ tone: "amber", text: "Unit ist gekündigt" });
  }
  if (ted) {
    const d = calendarDaysUntil(ted);
    if (d != null && d >= 0 && d <= 30) {
      warnings.push({
        tone: "amber",
        text: `Rückgabe an Vermieter in ${d} Tagen`,
      });
    }
  }
  if (ls === "ended" && (rtd == null || rtd === "")) {
    warnings.push({
      tone: "amber",
      text: "Vertrag beendet, aber keine Rückgabe erfasst",
    });
  }

  const leaseStartIso = parseIsoDate(
    unit?.leaseStartDate ?? unit?.lease_start_date
  );
  const todayIso = getTodayIsoForOccupancy();
  if (
    contractState !== "unknown" &&
    !metrics.leaseStarted &&
    metrics.currentRevenue <= 0 &&
    leaseStartIso != null &&
    leaseStartIso > todayIso
  ) {
    warnings.push({
      tone: "rose",
      text: "Mietbeginn (Vertrag Vermieter) liegt in der Zukunft und aktuell ist noch kein Umsatz gesichert.",
    });
  }

  if (
    contractState !== "unknown" &&
    metrics.currentRevenue != null &&
    metrics.currentRevenue <= 0
  ) {
    warnings.push({
      tone: "rose",
      text: "Keine aktuellen Einnahmen vorhanden.",
    });
  }

  if (isApartment) {
    if (
      metrics.apartmentTenanciesLoaded &&
      metrics.apartmentHasActiveTenancy === false
    ) {
      warnings.push({
        tone: "amber",
        text: "Wohnung leer",
      });
    }
  } else if (metrics.freeCount > 0) {
    warnings.push({
      tone: "amber",
      text: `${metrics.freeCount} freie Rooms ohne aktuelle Belegung.`,
    });
  }

  if (
    metrics.currentProfit != null &&
    metrics.currentProfit < 0
  ) {
    warnings.push({
      tone: "rose",
      text: `Aktuell unter Break-Even um ${formatCurrency(
        Math.abs(metrics.currentProfit)
      )}.`,
    });
  }

  if (isApartment) {
    // Step 4.5: getPhase4OperationalWarnings is DEPRECATED but kept because
    // the backend does not yet provide per-unit operational warnings (vacancy
    // risk, move-out countdown, upcoming bookings, lease collision, revenue
    // balance). Remove after backend extension — see the function's docstring.
    return mergeUnitWarningsByText(
      warnings,
      getPhase4OperationalWarnings(unit, rooms, unitTenancies, 30)
    ).slice(0, 12);
  }

  rooms.forEach((room) => {
    if (unitTenancies == null) return;
    const roomLabel = room.roomName || room.name || room.roomId || "Room";
    const rocc = getRoomOccupancyStatus(room, unitTenancies);
    const activeT = getActiveTenancyForRoom(room, unitTenancies, todayIso);
    const futureT = getFutureTenancyForRoom(room, unitTenancies, todayIso);
    const activeMoveIn = activeT ? parseIsoDate(activeT.move_in_date) : null;
    const futureMoveIn = futureT ? parseIsoDate(futureT.move_in_date) : null;

    if (
      rocc === "reserviert" &&
      (!room.reservedUntil || room.reservedUntil === "-") &&
      !futureMoveIn
    ) {
      warnings.push({
        tone: "amber",
        text: `${roomLabel}: Reserviert, aber ohne "Reserviert bis" Datum.`,
      });
    }

    if (
      rocc === "belegt" &&
      (!room.moveInDate || room.moveInDate === "-") &&
      !activeMoveIn
    ) {
      warnings.push({
        tone: "amber",
        text: `${roomLabel}: Belegt, aber ohne Einzugsdatum.`,
      });
    }

    if (
      rocc === "frei" &&
      (!room.freeFromDate || room.freeFromDate === "-")
    ) {
      warnings.push({
        tone: "amber",
        text: `${roomLabel}: Frei, aber ohne Frei-ab-Datum.`,
      });
    }
  });

  // Step 4.5: same DEPRECATED note as apartment path above.
  return mergeUnitWarningsByText(
    warnings,
    getPhase4OperationalWarnings(unit, rooms, unitTenancies, 30)
  ).slice(0, 12);
}
